import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
} from '@mui/material';
import {
  Calendar,
  CheckCircle,
  Clock,
  TrendingUp,
  Activity,
} from 'lucide-react';
import { SurveyData } from '../types';

interface RecentActivityProps {
  surveyData: SurveyData[];
}

const RecentActivity: React.FC<RecentActivityProps> = ({ surveyData }) => {
  const recentSurveys = surveyData.slice(-5).reverse();

  const getActivityIcon = (data: SurveyData) => {
    const avgScore = (data.energyLevel + data.sleepQuality + data.moodScore) / 3;
    if (avgScore >= 80) return <CheckCircle size={20} color="#00ff88" />;
    if (avgScore >= 60) return <TrendingUp size={20} color="#ffd700" />;
    return <Activity size={20} color="#ff6b6b" />;
  };

  const getActivityColor = (data: SurveyData) => {
    const avgScore = (data.energyLevel + data.sleepQuality + data.moodScore) / 3;
    if (avgScore >= 80) return '#00ff88';
    if (avgScore >= 60) return '#ffd700';
    return '#ff6b6b';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
          Recent Activity
        </Typography>
        
        {recentSurveys.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Clock size={48} color="#666" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No surveys completed yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete your first survey to see activity here
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {recentSurveys.map((survey, index) => {
              const avgScore = Math.round((survey.energyLevel + survey.sleepQuality + survey.moodScore) / 3);
              
              return (
                <React.Fragment key={survey.id}>
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getActivityIcon(survey)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDate(survey.date)}
                          </Typography>
                          <Chip
                            label={`${avgScore}%`}
                            size="small"
                            sx={{
                              backgroundColor: getActivityColor(survey),
                              color: '#000',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Energy: {survey.energyLevel}% | Sleep: {survey.sleepQuality}% | Mood: {survey.moodScore}%
                          </Typography>
                          {survey.exerciseType && survey.exerciseType !== 'None' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Exercise: {survey.exerciseType}
                            </Typography>
                          )}
                          {survey.supplements && survey.supplements.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Supplements: {survey.supplements.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < recentSurveys.length - 1 && <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />}
                </React.Fragment>
              );
            })}
          </List>
        )}

        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="body2" color="text.secondary">
            Total Surveys: {surveyData.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {surveyData.length > 0 && `Started ${new Date(surveyData[0].date).toLocaleDateString()}`}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecentActivity;
